import { useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SeriesSlot } from './GanttGroupRow';
import type { GroupBarSize } from '../../api/generated';
import type { TaggedInterventionGroup } from './types';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { MoveButtons } from './MoveButtons';
import { InterventionIconComponent } from '../InterventionIcon/InterventionIcon';
import { AddTreatmentPlanButton } from './AddTreatmentPlanButton';
import MenuDotsIcon from '../../assets/icons/menu-dots.svg?react';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import styles from './GanttCell.module.css';

export interface MoveHoverParams {
  scope: 'single' | 'series';
  groupId: string;
  treatmentPlanId: string;
  weekNumber: number;
  direction: 'left' | 'right';
}

interface GanttCellProps {
  slots: SeriesSlot[];
  seriesCount: number;
  isFirst?: boolean;
  isLast?: boolean;
  isEditMode?: boolean;
  readOnly?: boolean;
  onGroupClick?: (treatmentPlanId: string, groupId: string) => void;
  highlightedIds?: ReadonlySet<string>;
  onMoveHover?: (params: MoveHoverParams | null) => void;
  onMoveIntervention?: (params: MoveHoverParams) => void;
  movingGroupId?: string | null;
  onSetMovingGroupId?: (id: string | null, treatmentPlanId?: string) => void;
  onDeleteTreatmentPlan?: (treatmentPlanId: string) => void;
  onAddPatient?: (treatmentPlanId: string, groupId: string) => void;
  totalWeeks: number;
  onAddTreatmentPlanBatch?: (mode: 'single' | 'series') => void;
}

const GROUP_BLOCK_WIDTH: Record<GroupBarSize, string | undefined> = {
  LARGE: '129px',
  MEDIUM: '100px',
  SMALL: undefined
};

// A carried-over plan's border is dashed: it belongs to the previous year.
const resolveBlockBorder = (slot: SeriesSlot, isHighlighted: boolean): string => {
  if (isHighlighted) return `2px solid ${slot.highlightColor}`;
  const lineStyle = slot.originYear === undefined ? 'solid' : 'dashed';
  return `1px ${lineStyle} ${slot.strokeColor}`;
};

const GroupBlockContent = ({ group }: { group: TaggedInterventionGroup }) => {
  const patientCount = (group.assignedPatients ?? []).filter((p) => !p.shadow).length;
  const capacity = group.capacity ?? 0;
  const ratio = `${String(patientCount)}/${String(capacity)}`;

  return (
    <>
      <InterventionIconComponent icon={group.icon} className={styles.ivBlockIcon} />
      <span className={styles.ivBlockLabel}>
        {group.seriesLabel} {ratio}
      </span>
    </>
  );
};

export const GanttCell = ({
  slots,
  seriesCount,
  isFirst,
  isLast,
  isEditMode,
  readOnly,
  onGroupClick,
  highlightedIds,
  onMoveHover,
  onMoveIntervention,
  movingGroupId,
  onSetMovingGroupId,
  onAddTreatmentPlanBatch,
  onDeleteTreatmentPlan,
  onAddPatient,
  totalWeeks
}: GanttCellProps) => {
  const { t } = useTranslation();
  // Points the way the plan runs on: right into next year, left back into the previous one.
  const renderYearEdgeChevron = (color: string, isPointingLeft = false): ReactElement => (
    <ChevronIcon
      className={styles.yearEdgeChevron}
      style={{ color, ...(isPointingLeft ? { transform: 'rotate(180deg)' } : {}) }}
      aria-hidden="true"
    />
  );
  const canAssignPatient = useHasPermission(PERMISSIONS.PatientAssign);
  const [contextMenu, setContextMenu] = useState<{
    anchorRect: DOMRect;
    groupId: string;
    treatmentPlanId: string;
  } | null>(null);
  const [addPlanMenu, setAddPlanMenu] = useState<{ anchorRect: DOMRect } | null>(null);
  const moveWrapperRef = useRef<HTMLDivElement>(null);

  const hasContent = slots.some((s) => s.type !== 'empty');
  const className = isLast ? `${styles.cell} ${styles.lastCell}` : styles.cell;

  if (!hasContent) {
    return (
      <div className={`${className} ${styles.emptyCell}`}>
        {isEditMode && !readOnly && (
          <AddTreatmentPlanButton
            variant="empty-cell"
            wrapperClassName={`${styles.addButton} ${styles.addButtonCentered}`}
            isLast={isLast}
            onActivate={(rect) => setAddPlanMenu({ anchorRect: rect })}
          />
        )}
        {addPlanMenu &&
          createPortal(
            <ContextMenu
              anchorRect={addPlanMenu.anchorRect}
              items={[
                {
                  label: t('add-treatment-plan-single'),
                  onClick: () => onAddTreatmentPlanBatch?.('single')
                },
                {
                  label: t('add-treatment-plan-series'),
                  onClick: () => onAddTreatmentPlanBatch?.('series')
                }
              ]}
              onClose={() => setAddPlanMenu(null)}
            />,
            document.body
          )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={styles.stack}>
        {slots.map((slot) => {
          if (slot.type === 'connector') {
            return (
              <div key={slot.seriesIndex} className={styles.connectorSlot}>
                <div
                  className={[
                    styles.connectorLine,
                    slot.yearEdge === 'out' && styles.connectorFadeOut
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ backgroundColor: slot.strokeColor }}
                />
                {slot.yearEdge === 'out' && renderYearEdgeChevron(slot.strokeColor)}
              </div>
            );
          }

          if (slot.type === 'ivBlock') {
            const isCarriedOver = slot.originYear !== undefined;
            const isBlockEditable = !readOnly && !isCarriedOver;
            return (
              <div key={slot.seriesIndex} className={styles.ivBlockRow}>
                {slot.yearEdge === 'in' && renderYearEdgeChevron(slot.strokeColor, true)}
                <div
                  className={[styles.connectorArm, slot.yearEdge === 'in' && styles.connectorFadeIn]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    visibility:
                      slot.yearEdge !== 'in' && (isFirst || !slot.connectLeft)
                        ? 'hidden'
                        : undefined,
                    backgroundColor: slot.strokeColor
                  }}
                />
                <div className={styles.ivBlockGroup}>
                  {slot.groups.map((group, groupIdx) => {
                    const barSize = group.barSize ?? 'LARGE';
                    const width = GROUP_BLOCK_WIDTH[barSize];
                    const isMoving = movingGroupId === group.id;
                    const hasLeftNeighbor = groupIdx > 0;
                    const hasRightNeighbor = groupIdx < slot.groups.length - 1;
                    const isHighlighted =
                      highlightedIds && group.id
                        ? highlightedIds.has(group.id)
                        : false;
                    const isMenuOpen = contextMenu?.groupId === group.id;
                    const ivBlockCls = [
                      styles.ivBlock,
                      barSize === 'SMALL' && styles.ivBlockSmall,
                      barSize === 'MEDIUM' && styles.ivBlockMedium,
                      isHighlighted && styles.ivBlockHighlighted,
                      isCarriedOver && styles.ivBlockCarriedOver,
                      isBlockEditable && styles.ivBlockEditable,
                      isBlockEditable && isMenuOpen && styles.ivBlockMenuOpen
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const activateBlock = () => {
                      if (isMoving) {
                        onSetMovingGroupId?.(null);
                      } else if (group.id) {
                        onGroupClick?.(group.treatmentPlanId, group.id);
                      }
                    };
                    const openBlockMenu = (target: HTMLElement) => {
                      if (group.id) {
                        setContextMenu({
                          anchorRect: target.getBoundingClientRect(),
                          groupId: group.id,
                          treatmentPlanId: group.treatmentPlanId
                        });
                      }
                    };
                    const seriesPrefix =
                      seriesCount > 1 ? `#${String(slot.seriesIndex + 1)} · ` : '';
                    const blockTitle = `${seriesPrefix}${group.name ?? ''}`;
                    const blockStyle = {
                      ...(width ? { width } : {}),
                      backgroundColor: slot.fillColor,
                      border: resolveBlockBorder(slot, isHighlighted),
                      ...(slot.textColor ? { color: slot.textColor } : {})
                    };
                    const ivBlock = readOnly ? (
                      <div
                        key={group.id}
                        className={ivBlockCls}
                        title={blockTitle}
                        style={blockStyle}
                      >
                        <GroupBlockContent group={group} />
                      </div>
                    ) : (
                      <div key={group.id} className={ivBlockCls} title={blockTitle} style={blockStyle}>
                        <button
                          type="button"
                          className={styles.ivBlockAction}
                          aria-label={blockTitle}
                          onClick={(e) => {
                            e.stopPropagation();
                            activateBlock();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <GroupBlockContent group={group} />
                        </button>
                        {!isMoving && isBlockEditable && (
                          <button
                            type="button"
                            className={styles.ivBlockMenu}
                            aria-label={t('open-menu')}
                            aria-haspopup="menu"
                            aria-expanded={isMenuOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              openBlockMenu(e.currentTarget);
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <MenuDotsIcon />
                          </button>
                        )}
                      </div>
                    );

                    if (isMoving) {
                      return (
                        <MoveButtons
                          key={group.id}
                          group={group}
                          ivBlock={ivBlock}
                          hasLeftNeighbor={hasLeftNeighbor}
                          hasRightNeighbor={hasRightNeighbor}
                          seriesAtLeftEdge={slot.seriesMinWeek <= 1}
                          canMoveRight={slot.seriesMinWeek < totalWeeks}
                          moveWrapperRef={moveWrapperRef}
                          onMoveHover={onMoveHover}
                          onMoveIntervention={onMoveIntervention}
                          onAccept={() => onSetMovingGroupId?.(null)}
                        />
                      );
                    }

                    return ivBlock;
                  })}
                </div>
                <div
                  className={[styles.connectorArm, slot.yearEdge === 'out' && styles.connectorFadeOut]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    visibility:
                      slot.yearEdge !== 'out' && (isLast || !slot.connectRight)
                        ? 'hidden'
                        : undefined,
                    backgroundColor: slot.strokeColor
                  }}
                />
                {slot.yearEdge === 'out' && renderYearEdgeChevron(slot.strokeColor)}
              </div>
            );
          }

          return <div key={slot.seriesIndex} className={styles.emptySlot} />;
        })}
      </div>
      {isEditMode && !readOnly && (
        <AddTreatmentPlanButton
          variant="filled-cell"
          wrapperClassName={styles.addButton}
          lineWrapperClassName={styles.lineWrapper}
          isLast={isLast}
          onActivate={(rect) => setAddPlanMenu({ anchorRect: rect })}
        />
      )}
      {addPlanMenu &&
        createPortal(
          <ContextMenu
            anchorRect={addPlanMenu.anchorRect}
            items={[
              {
                label: t('add-treatment-plan-single'),
                onClick: () => onAddTreatmentPlanBatch?.('single')
              },
              {
                label: t('add-treatment-plan-series'),
                onClick: () => onAddTreatmentPlanBatch?.('series')
              }
            ]}
            onClose={() => setAddPlanMenu(null)}
          />,
          document.body
        )}
      {contextMenu &&
        createPortal(
          <ContextMenu
            anchorRect={contextMenu.anchorRect}
            items={[
              {
                label: t('open-module-details'),
                onClick: () => {
                  onGroupClick?.(contextMenu.treatmentPlanId, contextMenu.groupId);
                }
              },
              ...(isEditMode ? [
                {
                  label: t('move-module'),
                  onClick: () => {
                    onSetMovingGroupId?.(
                      contextMenu.groupId,
                      contextMenu.treatmentPlanId
                    );
                  }
                }
              ] : []),
              ...(canAssignPatient ? [
                {
                  label: t('add-patient'),
                  onClick: () => {
                    onAddPatient?.(contextMenu.treatmentPlanId, contextMenu.groupId);
                    setContextMenu(null);
                  }
                }
              ] : []),
              ...(isEditMode ? [
                {
                  label: t('delete-treatment-plan'),
                  onClick: () => {
                    onDeleteTreatmentPlan?.(contextMenu.treatmentPlanId);
                    setContextMenu(null);
                  }
                }
              ] : [])
            ]}
            onClose={() => {
              setContextMenu(null);
            }}
          />,
          document.body
        )}
    </div>
  );
};
